import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Container, Title, Text, Stack, Button, Group, Badge, Box, LoadingOverlay } from '@mantine/core'
import { RoutingProvider, useRouting, routingActions, UNDO_WINDOW_MS, type UndoEntry } from './contexts'
import { mockScenarios } from './utils'
import { RouterMatrix, LoginPage, SalvosPanel, UndoToast, LockConfirmModal } from './components'
import { useWebSocket, useAuth } from './hooks'
import { WS_URL, WS_API_URL, API_URL } from './config/env'
import { SalvosService } from './services/SalvosService'
import { getCurrentUser, type AuthUser } from './services/auth'
import type { SalvoCrosspoint } from '@mediaconnect-router/shared'

/**
 * Phase 7: prefer the API Gateway WebSocket endpoint when VITE_WS_API_URL
 * is set, attaching the Cognito access token as a query-string param.
 * Falls back to the legacy WS_URL for local dev.
 *
 * The hook reads the token synchronously from cognito's session, so a
 * refresh that happens after mount won't swap the URL until the next
 * render — that's fine for demo purposes; API GW authorizer caches keys.
 */
function useWsUrl(): string {
  const [url, setUrl] = useState<string>(WS_URL)
  useEffect(() => {
    const apiUrl = WS_API_URL
    if (!apiUrl) { setUrl(WS_URL); return }
    let cancelled = false
    getCurrentUser().then(u => {
      if (cancelled) return
      if (u?.accessToken) {
        setUrl(`${apiUrl}?token=${encodeURIComponent(u.accessToken)}`)
      } else {
        setUrl(apiUrl)
      }
    }).catch(() => { if (!cancelled) setUrl(apiUrl) })
    return () => { cancelled = true }
  }, [])
  return url
}

interface RouterInterfaceProps {
  salvos: SalvosService
  isAdmin: boolean
}

function makeUndoId(): string {
  // Short, unique-enough IDs. Avoids pulling in a uuid dep on the client.
  return `u-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

function RouterInterface({ salvos, isAdmin }: RouterInterfaceProps) {
  const { state, dispatch } = useRouting()
  const { sendConnectionRequest } = useWebSocket(useWsUrl(), dispatch)

  const loadMockData = (scenarioName: keyof typeof mockScenarios) => {
    const scenario = mockScenarios[scenarioName]()
    dispatch(routingActions.setSources(scenario.sources))
    dispatch(routingActions.setDestinations(scenario.destinations))
    dispatch(routingActions.setConnections(scenario.connections))
  }

  // Resolve a source or destination name for human-readable undo descriptions.
  const sourceName = useCallback((id: string) => state.routing.sources.find(s => s.id === id)?.name ?? id, [state.routing.sources])
  const destName = useCallback((id: string) => state.routing.destinations.find(d => d.id === id)?.name ?? id, [state.routing.destinations])

  // --- On-air derivation (Phase 6) ---
  // Destinations explicitly tagged onAir are "hot". Any source currently
  // routed to a hot destination inherits lock status so its entire column
  // warns. Both sets feed RouterMatrix for visual badges and the intercept
  // path in handleConnectionChange for the confirm modal.
  const onAirDestinationIds = useMemo(
    () => new Set(state.routing.destinations.filter(d => d.onAir).map(d => d.id)),
    [state.routing.destinations],
  )
  const lockedSourceIds = useMemo(() => {
    const locked = new Set<string>()
    for (const c of state.routing.connections) {
      if (onAirDestinationIds.has(c.destinationId) && c.status === 'connected') {
        locked.add(c.sourceId)
      }
    }
    return locked
  }, [state.routing.connections, onAirDestinationIds])

  // Holds a change that is waiting on user confirmation through the lock
  // modal. `null` when no modal is open.
  const [pendingLockChange, setPendingLockChange] = useState<
    | { sourceId: string; destinationId: string; connected: boolean; description: string }
    | null
  >(null)

  // Core apply path: optimistic update + REST/WS dispatch + undo push. Kept
  // as a separate function so both the direct-click path and the modal
  // confirm path go through exactly the same code.
  const applyConnectionChange = useCallback(async (sourceId: string, destinationId: string, connected: boolean) => {
    if (connected) {
      dispatch(routingActions.addConnection({
        sourceId, destinationId, status: 'connecting', timestamp: new Date(),
      }))
    } else {
      dispatch(routingActions.updateConnection({
        sourceId, destinationId, status: 'disconnecting', timestamp: new Date(),
      }))
    }
    try {
      await sendConnectionRequest(sourceId, destinationId, connected ? 'connect' : 'disconnect')
      const now = Date.now()
      const entry: UndoEntry = {
        id: makeUndoId(),
        description: connected
          ? `Connected ${sourceName(sourceId)} → ${destName(destinationId)}`
          : `Disconnected ${sourceName(sourceId)} → ${destName(destinationId)}`,
        createdAt: now,
        expiresAt: now + UNDO_WINDOW_MS,
        inverse: {
          kind: 'connection', sourceId, destinationId,
          action: connected ? 'disconnect' : 'connect',
        },
      }
      dispatch(routingActions.pushUndoEntry(entry))
    } catch (err) {
      if (connected) {
        dispatch(routingActions.removeConnection(sourceId, destinationId))
      } else {
        dispatch(routingActions.updateConnection({
          sourceId, destinationId, status: 'connected', timestamp: new Date(),
        }))
      }
      dispatch(routingActions.setError(err instanceof Error ? err.message : 'Connection request failed'))
    }
  }, [dispatch, sendConnectionRequest, sourceName, destName])

  const handleConnectionChange = async (sourceId: string, destinationId: string, connected: boolean) => {
    // Shift-click intercept: add/remove from the salvo-build selection
    // instead of actually changing routing.
    if (state.ui.shiftHeld) {
      dispatch(routingActions.toggleSalvoCrosspoint(sourceId, destinationId))
      return
    }

    // Phase 6 intercept: any change touching on-air content needs confirmation.
    const affectsOnAir = lockedSourceIds.has(sourceId) || onAirDestinationIds.has(destinationId)
    if (affectsOnAir) {
      const verb = connected ? 'Connect' : 'Disconnect'
      setPendingLockChange({
        sourceId,
        destinationId,
        connected,
        description: `${verb} ${sourceName(sourceId)} → ${destName(destinationId)}`,
      })
      return
    }

    await applyConnectionChange(sourceId, destinationId, connected)
  }

  // After firing a salvo, optimistically mark all its crosspoints as
  // connected, push an undo entry that covers every crosspoint (single entry,
  // as specified in the brief), then let the backend/WebSocket reconcile.
  const handleFireApplied = (crosspoints: SalvoCrosspoint[]) => {
    crosspoints.forEach(cp => {
      dispatch(routingActions.addConnection({
        sourceId: cp.sourceId,
        destinationId: cp.destinationId,
        status: 'connected',
        timestamp: new Date(),
      }))
    })
    const now = Date.now()
    const entry: UndoEntry = {
      id: makeUndoId(),
      description: `Fired salvo (${crosspoints.length} crosspoint${crosspoints.length === 1 ? '' : 's'})`,
      createdAt: now,
      expiresAt: now + UNDO_WINDOW_MS,
      inverse: { kind: 'salvo-fire', crosspoints: [...crosspoints] },
    }
    dispatch(routingActions.pushUndoEntry(entry))
  }

  // The undo executor consumes the most recent undoable entry and dispatches
  // its declarative inverse through the existing REST/WebSocket path. This
  // is a callback so the keyboard handler and the toast button share logic.
  const executeLatestUndo = useCallback(async () => {
    const top = state.ui.undoBuffer[state.ui.undoBuffer.length - 1]
    if (!top) return
    if (top.expiresAt <= Date.now()) {
      // Expired in the small window between render and click — drop it.
      dispatch(routingActions.removeUndoEntry(top.id))
      return
    }
    // Remove first so a rapid second Cmd+Z doesn't re-trigger the same entry.
    dispatch(routingActions.removeUndoEntry(top.id))

    try {
      if (top.inverse.kind === 'connection') {
        const { sourceId, destinationId, action } = top.inverse
        await sendConnectionRequest(sourceId, destinationId, action)
        // Mirror optimistic state for responsiveness.
        if (action === 'disconnect') {
          dispatch(routingActions.removeConnection(sourceId, destinationId))
        } else {
          dispatch(routingActions.addConnection({
            sourceId, destinationId, status: 'connected', timestamp: new Date(),
          }))
        }
      } else if (top.inverse.kind === 'salvo-fire') {
        for (const cp of top.inverse.crosspoints) {
          try {
            await sendConnectionRequest(cp.sourceId, cp.destinationId, 'disconnect')
            dispatch(routingActions.removeConnection(cp.sourceId, cp.destinationId))
          } catch {
            // Best-effort: keep going through the rest of the crosspoints.
          }
        }
      }
    } catch (err) {
      dispatch(routingActions.setError(err instanceof Error ? err.message : 'Undo failed'))
    }
  }, [state.ui.undoBuffer, sendConnectionRequest, dispatch])

  // Global Cmd/Ctrl+Z keybind.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isUndo = (e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'z'
      if (!isUndo) return
      // Ignore if user is typing into an input or textarea.
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      e.preventDefault()
      executeLatestUndo()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [executeLatestUndo])

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Title order={1}>MediaConnect Router</Title>
        <Text size="lg" c="dimmed">
          Core XY Router Interface - RouterMatrix Component Ready
        </Text>

        <Group gap="sm">
          <Text fw={500}>Load Mock Data:</Text>
          <Button size="xs" onClick={() => loadMockData('smallStudio')}>Small Studio</Button>
          <Button size="xs" onClick={() => loadMockData('mediumFacility')}>Medium Facility</Button>
          <Button size="xs" onClick={() => loadMockData('largeBroadcastCenter')}>Large Center</Button>
          <Button size="xs" onClick={() => loadMockData('minimal')}>Minimal</Button>
        </Group>

        <Group gap="lg">
          <Badge color="blue" size="lg">Sources: {state.routing.sources.length}</Badge>
          <Badge color="green" size="lg">Destinations: {state.routing.destinations.length}</Badge>
          <Badge color="orange" size="lg">Connections: {state.routing.connections.length}</Badge>
          {state.ui.shiftHeld && (
            <Badge color="violet" size="lg">Shift held — salvo-build mode</Badge>
          )}
        </Group>

        <Group align="flex-start" gap="md" wrap="nowrap">
          <Box style={{ flex: 1, minWidth: 0 }}>
            {state.routing.sources.length > 0 && (
              <RouterMatrix
                sources={state.routing.sources}
                destinations={state.routing.destinations}
                connections={state.routing.connections}
                onConnectionChange={handleConnectionChange}
                loading={state.routing.loading}
                error={state.routing.error || undefined}
                lockedSourceIds={lockedSourceIds}
                onAirDestinationIds={onAirDestinationIds}
              />
            )}
            {state.routing.sources.length === 0 && (
              <Text size="sm" c="dimmed">
                Load mock data to see the RouterMatrix component in action.
              </Text>
            )}
          </Box>
          <SalvosPanel
            service={salvos}
            isAdmin={isAdmin}
            onFireApplied={handleFireApplied}
            onError={(msg) => dispatch(routingActions.setError(msg))}
          />
        </Group>
      </Stack>

      <UndoToast onUndo={executeLatestUndo} />
      <LockConfirmModal
        opened={pendingLockChange !== null}
        description={pendingLockChange?.description ?? ''}
        onCancel={() => setPendingLockChange(null)}
        onConfirm={async () => {
          if (!pendingLockChange) return
          const p = pendingLockChange
          setPendingLockChange(null)
          await applyConnectionChange(p.sourceId, p.destinationId, p.connected)
        }}
      />
    </Container>
  )
}

function AuthenticatedApp() {
  const { user, loading, error, signIn, signOut, authConfigured } = useAuth();

  const userRef = useRef<AuthUser | null>(null);
  userRef.current = user;

  const salvosService = useMemo(
    () => new SalvosService(API_URL, () => userRef.current?.accessToken ?? null),
    [],
  );

  const isAdmin = useMemo(() => {
    if (!user?.accessToken) return false;
    try {
      const [, payload] = user.accessToken.split('.');
      if (!payload) return false;
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      const groups = decoded['cognito:groups'];
      return Array.isArray(groups) && groups.includes('admins');
    } catch {
      return false;
    }
  }, [user?.accessToken]);

  if (!authConfigured) {
    return (
      <RoutingProvider>
        <RouterInterface salvos={salvosService} isAdmin={false} />
      </RoutingProvider>
    );
  }
  if (loading) return <LoadingOverlay visible />;
  if (!user) return <LoginPage onSubmit={signIn} error={error} loading={loading} />;

  return (
    <RoutingProvider>
      <Group justify="flex-end" p="xs">
        {isAdmin && <Badge color="red" size="sm">admin</Badge>}
        <Button variant="subtle" size="xs" onClick={signOut}>Sign out ({user.email ?? user.username})</Button>
      </Group>
      <RouterInterface salvos={salvosService} isAdmin={isAdmin} />
    </RoutingProvider>
  );
}

function App() {
  return <AuthenticatedApp />;
}

export default App
